import { Box, Modal, Pressable, Text } from 'native-base'

import { X } from '@phosphor-icons/react'

export type DriverInfoStage = 'ask' | 'confirmed'

interface DriverInfoModalProps {
  visible: boolean
  stage: DriverInfoStage
  onConfirm: () => void
  onDecline: () => void
  onClose: () => void
}

/**
 * Fake-door experiment popup (driver info). Shown once when a consumer arrives
 * on the tracking page via the `driverInfo=1` link in the one-stop-away SMS.
 * There is no real feature behind it — we're measuring demand.
 *
 * Two stages, because arriving here is ambiguous: the link is the same tracking
 * link the SMS always carries, so a tap may only mean "show me my ETA". The
 * `ask` stage puts the question directly, and its answer is what the experiment
 * counts; `confirmed` acknowledges a yes.
 *
 * Both answers are given equal visual weight on purpose. A prominent "yes"
 * against a dismiss affordance would bias the response, and this popup exists
 * to measure that response — so the two options match in size and prominence,
 * and "no" is a real tracked answer rather than an inferred dismissal.
 */
const DriverInfoModal = ({ visible, stage, onConfirm, onDecline, onClose }: DriverInfoModalProps) => (
  // closeOnOverlayClick is off so the only ways out are the buttons. The popup
  // nearly fills a phone screen, so backdrop taps are often accidental, and an
  // accidental dismissal is indistinguishable in the data from a deliberate one.
  // Escape still closes it — that is a deliberate act, and removing it would be
  // an accessibility regression.
  <Modal isOpen={visible} onClose={onClose} closeOnOverlayClick={false}>
    <Modal.Content maxWidth="360px" maxH="90%" borderRadius="16px">
      <Box px={5} pt={stage === 'ask' ? 5 : 6} pb={6}>
        {stage === 'ask' ? (
          <>
            {/* Header row: dismissing without answering is a third outcome, distinct
                from either button, so the close affordance stays on this stage. */}
            <Box flexDirection="row" justifyContent="flex-end" alignItems="center" mb={2}>
              <Pressable onPress={onClose} p={1} accessibilityLabel="Close modal">
                <X size={20} color="#374151" />
              </Pressable>
            </Box>

            <Text fontSize={20} fontWeight="700" color="#111827" mb={3} lineHeight={28}>
              Want to see your driver&apos;s info?
            </Text>
            <Text fontSize={14} color="#374151" mb={6} lineHeight={22}>
              We&apos;re considering showing you your driver&apos;s name and photo before they arrive.
            </Text>

            <Pressable
              onPress={onConfirm}
              backgroundColor="#C0392B"
              borderRadius="full"
              py={3}
              mb={3}
              alignItems="center"
              accessibilityRole="button"
            >
              <Text color="white" fontWeight="700" fontSize={16}>
                Yes, I&apos;d use this
              </Text>
            </Pressable>
            <Pressable
              onPress={onDecline}
              backgroundColor="#374151"
              borderRadius="full"
              py={3}
              alignItems="center"
              accessibilityRole="button"
            >
              <Text color="white" fontWeight="700" fontSize={16}>
                No thanks
              </Text>
            </Pressable>
          </>
        ) : (
          <>
            {/* No close icon here: "Got it" is the single action, and a large target
                beats a 20px corner icon on a phone. */}
            <Text fontSize={20} fontWeight="700" color="#111827" mb={3} lineHeight={28}>
              Thanks for your interest!
            </Text>
            <Text fontSize={14} color="#374151" mb={6} lineHeight={22}>
              Thank you for your interest in learning more about your driver. We&apos;re considering adding this feature
              in the near future.
            </Text>
            <Pressable
              onPress={onClose}
              backgroundColor="#C0392B"
              borderRadius="full"
              py={3}
              alignItems="center"
              accessibilityRole="button"
            >
              <Text color="white" fontWeight="700" fontSize={16}>
                Got it
              </Text>
            </Pressable>
          </>
        )}
      </Box>
    </Modal.Content>
  </Modal>
)

export default DriverInfoModal
